/**
 * 검색 영역의 드롭다운(시술·계정·강의 범위).
 *
 * 여닫는 것은 <details>/<summary> 에 맡긴다 — 스크립트가 없어도 열리고 키보드로
 * 조작되며, 안에 든 것은 그냥 라디오·체크박스라 폼이 그대로 전송된다.
 * 여기서 더하는 것은 브라우저가 해주지 않는 네 가지뿐이다:
 *
 *   1. 바깥을 누르거나 Esc 를 누르면 닫기(한 번에 하나만 열어 둔다)
 *   2. 하나만 고르는 드롭다운은 고른 즉시 닫기
 *   3. 토글에 적힌 요약 문구를 고른 값에 맞춰 다시 쓰기
 *   4. 열린 목록을 화면 기준(fixed)으로 띄우기 — 아래 참고
 *
 * 요약 문구 규칙은 서버(qrshop_academy_accounts_label)와 같아야 한다.
 * 고른 게 없으면 data-academy-dropdown-empty, 하나면 그 항목 이름,
 * 여럿이면 '첫 항목 외 n개'.
 *
 * 왜 fixed 인가 — 알약 줄(.academy-filters__row)은 좌우로 스크롤되는 상자다.
 * 가로 스크롤 상자는 세로로도 잘라내기 때문에, 상자 안에 absolute 로 놓인 목록은
 * 줄 높이에서 잘려 보이지 않는다. 열린 동안만 목록을 화면 기준으로 띄워 상자
 * 밖으로 꺼낸다. CSS 로 상자의 잘라내기를 푸는 방법도 있지만, 그러면 그 순간
 * 상자가 스크롤 컨테이너가 아니게 되어 옆으로 밀어 둔 위치가 처음으로 돌아간다.
 *
 * 스크립트가 없을 때는 CSS 가 같은 일을 대신한다(:root:not(.has-js-dropdown)).
 */

const PANEL_GAP = 4; // 토글과 목록 사이(px)
const EDGE = 8; // 화면 가장자리에서 최소한 띄울 거리(px)

/**
 * 열린 목록을 토글 바로 아래, 오른쪽 끝을 맞춰 띄운다.
 * 화면 왼쪽으로 삐져나가면 그만큼 안으로 밀어 넣는다.
 *
 * data-academy-dropdown-anchor="header" 를 달면 토글이 아니라 헤더 아래에 붙는다
 * (계정 메뉴처럼 헤더에서 이어져 내려오는 것처럼 보여야 하는 경우).
 * 높이를 값으로 적지 않고 헤더를 재는 이유는, 헤더 높이가 바뀌어도 따라가게 하기 위해서다.
 */
const placePanel = (dropdown) => {
  const panel = dropdown.querySelector('.academy-dropdown__panel');
  const toggle = dropdown.querySelector('summary');

  if (!panel || !toggle) {
    return;
  }

  const rect = toggle.getBoundingClientRect();
  // 화면 폭은 clientWidth 로 잰다 — innerWidth 는 스크롤바까지 포함해 오른쪽이 밀린다.
  const viewport = document.documentElement.clientWidth;
  const header =
    dropdown.dataset.academyDropdownAnchor === 'header'
      ? document.querySelector('.site-header')?.getBoundingClientRect()
      : null;

  panel.style.position = 'fixed';
  panel.style.top = `${Math.round(header ? header.bottom : rect.bottom + PANEL_GAP)}px`;
  panel.style.left = 'auto';
  panel.style.right = `${Math.round(viewport - rect.right)}px`;

  // 토글에 붙는 목록만 최소 폭을 토글에 맞춘다(CSS 의 min-w-full 은 fixed 에서
  // 화면 기준이 되어 못 쓴다). 헤더에 붙는 목록은 제 폭을 CSS 가 정한다.
  if (!header) {
    panel.style.minWidth = `${Math.round(rect.width)}px`;
  }

  const panelBox = panel.getBoundingClientRect();

  if (panelBox.left < EDGE) {
    panel.style.right = 'auto';
    panel.style.left = `${EDGE}px`;
  }

  // 자리가 잡힌 뒤에야 보인다. 이 표시가 없으면 브라우저가 CSS 기본 자리(토글 바로
  // 아래, absolute)에 한 번 그린 뒤 여기서 옮기게 되어 목록이 위에서 내려오는 것처럼 보인다.
  panel.classList.add('is-placed');
};

const clearPanel = (dropdown) => {
  const panel = dropdown.querySelector('.academy-dropdown__panel');

  if (panel) {
    panel.removeAttribute('style');
    panel.classList.remove('is-placed');
  }
};

const setLabel = (dropdown) => {
  const label = dropdown.querySelector('[data-academy-dropdown-label]');

  if (!label) {
    return;
  }

  const checked = [...dropdown.querySelectorAll('.academy-dropdown__control:checked')];
  const first = checked[0]
    ?.closest('.academy-dropdown__option')
    ?.querySelector('.academy-dropdown__text')
    ?.textContent.trim();

  if (!first) {
    label.textContent = dropdown.dataset.academyDropdownEmpty || '';
    return;
  }

  label.textContent = checked.length > 1 ? `${first} 외 ${checked.length - 1}개` : first;
};

/**
 * 폼 밖에서 조건이 바뀐 뒤(태그 칩·필터 초기화 등) 토글 문구를 다시 맞춘다.
 */
export const syncDropdownLabels = (root = document) => {
  root.querySelectorAll('[data-academy-dropdown]').forEach(setLabel);
};

const InitAcademyDropdown = () => {
  // 검색 폼은 결과만 갈아끼우는 방식이라 다시 그려지지 않는다 — 한 번 모아두면 된다.
  const dropdowns = [...document.querySelectorAll('[data-academy-dropdown]')];

  if (!dropdowns.length) {
    return;
  }

  // CSS 쪽 대체 규칙을 끈다 — 이제 목록 자리는 이 파일이 잡는다.
  document.documentElement.classList.add('has-js-dropdown');

  const closeAll = (except) => {
    dropdowns.forEach((dropdown) => {
      if (dropdown !== except) {
        dropdown.open = false;
      }
    });
  };

  dropdowns.forEach((dropdown) => {
    dropdown.addEventListener('toggle', () => {
      if (dropdown.open) {
        closeAll(dropdown);
        placePanel(dropdown);
        return;
      }

      clearPanel(dropdown);
    });

    dropdown.addEventListener('change', () => {
      setLabel(dropdown);

      // 여러 개 고르는 쪽은 연달아 고를 수 있게 열어 둔다.
      if (dropdown.dataset.academyDropdown !== 'multi') {
        dropdown.open = false;
      }
    });
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-academy-dropdown]')) {
      closeAll();
    }
  });

  /*
   * 화면이 움직이면 자리를 다시 잡는다 — 페이지 스크롤, 알약 줄 가로 스크롤,
   * 창 크기 변경 모두 같은 처리다. capture 로 받아야 알약 줄처럼 안쪽 상자에서
   * 나는 스크롤까지 잡힌다(scroll 은 위로 올라오지 않는다).
   */
  const reposition = () => {
    const open = dropdowns.find((dropdown) => dropdown.open);

    if (open) {
      placePanel(open);
    }
  };

  document.addEventListener('scroll', reposition, { capture: true, passive: true });
  window.addEventListener('resize', reposition);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    const open = dropdowns.find((dropdown) => dropdown.open);

    if (!open) {
      return;
    }

    open.open = false;
    open.querySelector('summary')?.focus(); // 닫고 나서 초점이 페이지 밖으로 새지 않게
  });
};

export default InitAcademyDropdown;
